import { Clock3 } from "lucide-react";

import { Button } from "@/components/ui/button";
import type { TrialAccessViewModel } from "@/hooks/useTrialAccess";
import { cn } from "@/lib/utils";

export function TrialToolbarControl({
  trial,
  compact = false,
  onExplore,
  onOpenPricing,
}: {
  trial: TrialAccessViewModel;
  compact?: boolean;
  onExplore?: () => void;
  onOpenPricing: () => void;
}) {
  const { presentation } = trial;
  if (
    trial.isLoading ||
    presentation.state === "paid" ||
    presentation.state === "comp" ||
    presentation.state === "coach_led" ||
    presentation.state === "billing_transfer_pending" ||
    presentation.state === "preview" ||
    presentation.state === "unknown" ||
    presentation.state === "expired" ||
    !presentation.message ||
    !presentation.cta
  ) {
    return null;
  }

  const exploresValue = presentation.cta.variant === "explore_coach_veera";
  const action = exploresValue && onExplore ? onExplore : onOpenPricing;
  const accessibleLabel = `${presentation.message.replace(/\s*·\s*/, ", ")}. ${presentation.cta.label}`;
  const exactEndDate = presentation.trialEndsAt
    ? new Intl.DateTimeFormat(undefined, {
        dateStyle: "medium",
        timeZone: trial.timeZone,
      }).format(new Date(presentation.trialEndsAt))
    : undefined;

  return (
    <Button
      type="button"
      variant="outline"
      size="sm"
      aria-label={accessibleLabel}
      title={exactEndDate ? `Full access through ${exactEndDate}` : undefined}
      onClick={action}
      className={cn(
        "h-9 max-w-full gap-1.5 border-primary/30 bg-primary/[0.07] px-2.5 text-primary shadow-none hover:bg-primary/[0.12]",
        !compact && "px-3",
      )}
    >
      <Clock3 className="h-3.5 w-3.5 shrink-0" />
      <span className="truncate text-xs font-semibold">{presentation.message}</span>
      {!compact ? (
        <span className="hidden border-l border-primary/20 pl-2 text-xs font-medium lg:inline">
          {presentation.cta.label}
        </span>
      ) : null}
    </Button>
  );
}
